import {computed, makeObservable, observable, runInAction} from "mobx";
import {ISparkSwiperController, ISparkSwiperControllerOptions} from "./spark-swiper-controller.interface";
import {ISparkSwiperSlideController, SwipeDirectionEnum} from "./spark-swiper-slide-controller.interface";
import {ISparkSwiperDataFeeder} from "./spark-swiper-data-feeder.interface";


export class SparkSwiperController implements ISparkSwiperController {
    constructor(private readonly dataFeeder: ISparkSwiperDataFeeder, options?: Partial<ISparkSwiperControllerOptions>) {
        this._options = {
            middleSlideControllerPosition: 2,
            ...options
        };
        makeObservable(this);
    }

    private readonly _options: ISparkSwiperControllerOptions;

    getCurrentItemIndex(): number {
        return this.dataFeeder.getCurrentItemIndex();
    }

    private setCurrentItemIndex(index: number): void {
        this.dataFeeder.setCurrentItemIndex(index);
    }

    getItemsCount(): number {
        return this.dataFeeder.getItemsCount();
    }

    get maxItemIndex(): number {
        return this.dataFeeder.getMaxItemIndex();
    }


    get middleSlidePosition(): number {
        return Math.min(this._options.middleSlideControllerPosition, Math.floor(this.getItemsCount()/2));
    }


    findSlidePosition(slideController: ISparkSwiperSlideController): number {
        return this.slides.findIndex(s => s === slideController);
    }

    private _generateSlidesControllers(): ISparkSwiperSlideController[] {
        const maxSlides = this._options.middleSlideControllerPosition * 2;
        const result: ISparkSwiperSlideController[] = [];
        for(let i = 0; i <= maxSlides; i++) {
            result.push(this.dataFeeder.createSlideController(i.toString(), this));
        }

        return result;
    }



    @observable.ref
    private _slidesControllers: ISparkSwiperSlideController[] | null = null;


    @computed
    get slides(): ISparkSwiperSlideController[] {

        const itemsCount = this.getItemsCount();

        if(itemsCount === 0) {
            return [];
        }

        if(!this._slidesControllers) {
            this._slidesControllers = this._generateSlidesControllers();
        }

        if(itemsCount < this._slidesControllers.length) {
            return this._slidesControllers.slice(0, itemsCount)
        }

       return this._slidesControllers;
    }

    private get activeSlideController(): ISparkSwiperSlideController {
        return this.slides.find(s => s.isActiveSlide);
    }

    moveNext(): void {
        if(this.getCurrentItemIndex() < this.maxItemIndex) {

            this.activeSlideController.onBeforeDeactivate(SwipeDirectionEnum.Forward);

            runInAction(() => {
                this.setCurrentItemIndex(this.getCurrentItemIndex() + 1);
                if(this.getCurrentItemIndex() > this.middleSlidePosition && this.getCurrentItemIndex() <= this.maxItemIndex - this.middleSlidePosition) {
                    this._rotateSlideController(0, this.slides.length - 1);
                }

                this.activeSlideController.onAfterActivated(SwipeDirectionEnum.Forward);

            });
        }
    }

    movePrevious(): void {
        if(this.getCurrentItemIndex() > 0) {
            this.activeSlideController.onBeforeDeactivate(SwipeDirectionEnum.Backward);
            runInAction(() => {
                this.setCurrentItemIndex(this.getCurrentItemIndex() - 1);
                if(this.getCurrentItemIndex() >= this.middleSlidePosition && this.getCurrentItemIndex() < this.maxItemIndex - this.middleSlidePosition) {
                    this._rotateSlideController(this.slides.length - 1, 0);
                }
                this.activeSlideController.onAfterActivated(SwipeDirectionEnum.Backward);
            });
        }
    }

    private _rotateSlideController(from: number, to: number) {

        //console.error(`move from ${from} to ${to}`);
        const cloned = [...this._slidesControllers];
        const moved = this.slides[from];
        cloned.splice(from, 1);
        cloned.splice(to, 0, moved);
        this._slidesControllers = cloned;

    }
}